import {DocumentContext, DocumentEntityType, DocumentProcessingStatus} from '@sollapay/enums';
import {useQueryClient} from '@tanstack/react-query';
import {useEffect, useMemo, useRef, useState} from 'react';

import {useAiFormSession} from '@/features/ai-extraction';
import {
  documentProcessingKeys,
  getLatestDocument,
  useDocumentsProcessingStatuses,
  useDocumentsQueriesByContext
} from '@/features/documents';

import type {AiSuggestion} from '@/features/ai-extraction';
import type {DocumentType} from '@sollapay/enums';
import type {FieldValues, UseFormGetValues, UseFormSetValue} from 'react-hook-form';

export type FormStateSnapshot = {
  isDirty: boolean;
  isLoading: boolean;
  isSaving: boolean;
  isError: boolean;
  hasErrors: boolean;
  /** The step can't be filled in yet — shown as a lock on its tab instead of an error. */
  isLocked?: boolean;
  onSave: () => Promise<void>;
  onCancel: () => void;
};

/** The per-step slice a parent tracks for every step, not just the active one. */
export type StepStatus = Pick<FormStateSnapshot, 'hasErrors' | 'isLocked'>;

export const INITIAL_FORM_STATE: FormStateSnapshot = {
  isDirty: false,
  isLoading: false,
  isSaving: false,
  isError: false,
  hasErrors: false,
  onSave: () => Promise.resolve(),
  onCancel: () => {}
};

/** Omitted `documentContext`/`documentTypes` fall back to the ai config's own. */
export interface AiDocumentScope {
  id: string;
  metadata: Record<string, string | undefined>;
  documentContext?: DocumentContext;
  documentTypes?: readonly DocumentType[];
}

/**
 * AI extraction wiring for a form. `documentTypes` must be defined at module scope to keep its
 * reference stable across renders.
 *
 * Scopes are addressed by id, never by position, so a field-array caller can add or remove rows
 * without a scope's suggestions, processing state, or stopped flag moving to another row. One scope
 * per session narrows a shared document namespace (the buyer-add flow); one scope per row keeps
 * each row independent (the seller roster). Omitting `documentScopes` yields a single unfiltered
 * scope; an empty array yields none.
 */
export interface UseFormShellAiConfig<TFieldValues extends FieldValues = FieldValues> {
  entityType: DocumentEntityType;
  entityId: string;
  documentContext: DocumentContext;
  documentTypes: readonly DocumentType[];
  documentScopes?: AiDocumentScope[];
  getValues: UseFormGetValues<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  resolveField?: (targetField: string, scopeId: string) => string | null;
  toFieldValue?: (field: string, extractedValue: string) => string;
}

export interface UseFormShellParams<TFieldValues extends FieldValues = FieldValues> {
  isDirty: boolean;
  isLoading: boolean;
  isSaving: boolean;
  isError: boolean;
  /** True when the save mutation failed. Used to suppress submitEvaluations on save failure. */
  isSaveError?: boolean;
  hasErrors?: boolean;
  isReadOnly?: boolean;
  onSave: () => Promise<void>;
  onCancel: () => void;
  onFormStateChange?: (state: FormStateSnapshot) => void;
  ai?: UseFormShellAiConfig<TFieldValues>;
}

// Stable no-ops used when AI is not configured, avoiding new allocations per render.
const noopGetValues = (() => ({})) as UseFormGetValues<FieldValues>;
const noopSetValue = (() => {}) as UseFormSetValue<FieldValues>;

const DEFAULT_DOCUMENT_SCOPES: AiDocumentScope[] = [{id: '', metadata: {}}];

/**
 * Encapsulates AI extraction wiring and form-state sync.
 * Queries documents, polls processing status, manages AI session,
 * computes effectiveDirty, and syncs state to the parent via onFormStateChange.
 */
export function useFormShell<TFieldValues extends FieldValues = FieldValues>({
  isDirty,
  isLoading,
  isSaving,
  isError,
  isSaveError,
  hasErrors = false,
  isReadOnly = false,
  onSave,
  onCancel,
  onFormStateChange,
  ai
}: UseFormShellParams<TFieldValues>): {
  aiFields: Map<string, AiSuggestion>;
  isProcessing: boolean;
  processingScopes: ReadonlySet<string>;
  suggestionsCountByScope: ReadonlyMap<string, number>;
  suggestionsCount: number;
  markFieldModified: (field: string) => void;
  handleCancelWithAI: () => void;
  stopScope: (scopeId: string) => void;
  stopAllScopes: () => void;
  reindexFieldArray: (params: {fieldArrayPrefix: string; removedIndex: number}) => void;
} {
  const queryClient = useQueryClient();

  // ─── Document query + processing status polling ────────────────────────────
  // Always called (Rules of Hooks); disabled via enabled:false / empty array when ai is absent.

  // Only an absent scope list falls back to the unfiltered default; an empty one polls nothing.
  const scopes = ai?.documentScopes ?? DEFAULT_DOCUMENT_SCOPES;
  const defaultContext = ai?.documentContext ?? DocumentContext.TRUST_DETAILS;
  const documentContexts = [
    ...new Set([defaultContext, ...scopes.map(scope => scope.documentContext ?? defaultContext)])
  ];

  const {documentsByContext, updatedAtKey} = useDocumentsQueriesByContext(
    ai?.entityType ?? DocumentEntityType.TRUST_ACCOUNT,
    ai?.entityId ?? '',
    documentContexts
  );

  // String keys prevent useMemo from thrashing when array/object references change.
  const documentTypesKey = (ai?.documentTypes ?? []).join(',');
  const scopesKey = JSON.stringify(scopes);

  // Each slot carries its scope id so no consumer has to re-derive the mapping from position.
  // `liveDocumentIds` holds every listed document, not just the latest per type that gets polled.
  const {documentSlots, scopeIdByDocumentId, liveDocumentIds} = useMemo(() => {
    const slots: Array<{documentId: string | undefined; scopeId: string}> = [];
    const byDocumentId = new Map<string, string>();
    const liveIds = new Set(
      [...documentsByContext.values()].flatMap(documents => documents?.map(d => d.id) ?? [])
    );
    if (!ai) {
      return {documentSlots: slots, scopeIdByDocumentId: byDocumentId, liveDocumentIds: liveIds};
    }

    for (const scope of scopes) {
      const documents = documentsByContext.get(scope.documentContext ?? defaultContext);
      for (const documentType of scope.documentTypes ?? ai.documentTypes) {
        const documentId = getLatestDocument(documents, documentType, scope.metadata)?.id;
        slots.push({documentId, scopeId: scope.id});
        if (documentId) byDocumentId.set(documentId, scope.id);
      }
    }

    return {documentSlots: slots, scopeIdByDocumentId: byDocumentId, liveDocumentIds: liveIds};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedAtKey, documentTypesKey, scopesKey]);

  // Stopping applies to the documents being read at that moment — a document uploaded later has a new id and is read again.
  const [stoppedDocumentIds, setStoppedDocumentIds] = useState<ReadonlySet<string>>(
    () => new Set<string>()
  );
  const isStopped = (documentId: string | undefined): boolean =>
    documentId !== undefined && stoppedDocumentIds.has(documentId);

  // Excludes the scope list so that adding a row cannot un-stop an already-stopped document.
  const sessionIdentityKey = `${ai?.entityId ?? ''}|${documentTypesKey}`;
  const prevSessionIdentityRef = useRef(sessionIdentityKey);
  if (prevSessionIdentityRef.current !== sessionIdentityKey) {
    prevSessionIdentityRef.current = sessionIdentityKey;
    setStoppedDocumentIds(new Set<string>());
  }

  // Stopped documents keep their ids and are disabled per index, so resuming reads cache, not network.
  // A read-only form can't take a suggestion, so it polls nothing.
  const processingStatuses = useDocumentsProcessingStatuses(
    documentSlots.map(slot => slot.documentId),
    {enabled: documentSlots.map(slot => !isReadOnly && !isStopped(slot.documentId))}
  );

  // ─── AI session ────────────────────────────────────────────────────────────

  const {
    aiFields,
    suggestionsCount,
    suggestionsCountByScope,
    markFieldModified,
    submitEvaluations,
    clearAiState,
    reindexFieldArray
  } = useAiFormSession<TFieldValues>({
    documentStatuses: processingStatuses.map((result, i) =>
      isStopped(documentSlots[i]?.documentId) ? undefined : result.data
    ),
    getValues: (ai?.getValues ?? noopGetValues) as UseFormGetValues<TFieldValues>,
    setValue: (ai?.setValue ?? noopSetValue) as UseFormSetValue<TFieldValues>,
    resolveField: ai?.resolveField,
    toFieldValue: ai?.toFieldValue,
    scopeIdByDocumentId,
    liveDocumentIds
  });

  // ─── Derived state ────────────────────────────────────────────────────────

  const processingScopes = new Set<string>();
  processingStatuses.forEach((result, flatIndex) => {
    const slot = documentSlots[flatIndex];
    if (!slot || isStopped(slot.documentId)) return;
    const status = result.data?.processingStatus;
    if (
      status === DocumentProcessingStatus.PENDING ||
      status === DocumentProcessingStatus.PROCESSING
    ) {
      processingScopes.add(slot.scopeId);
    }
  });

  const isProcessing = processingScopes.size > 0;

  const effectiveDirty = isDirty || suggestionsCount > 0;

  // Latest-value refs keep handleCancelWithAI's identity stable.
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const clearAiStateRef = useRef(clearAiState);
  clearAiStateRef.current = clearAiState;

  const handleCancelWithAI = useMemo(
    () => () => {
      onCancelRef.current();
      clearAiStateRef.current();
    },
    []
  );

  /** Cancels status polling for the documents currently in the matching scopes and stops reading them. */
  const stopDocuments = (matchesScope: (scopeId: string) => boolean): void => {
    const documentIds: string[] = [];
    for (const {documentId, scopeId} of documentSlots) {
      if (!documentId || !matchesScope(scopeId)) continue;
      void queryClient.cancelQueries({queryKey: documentProcessingKeys.byDocument(documentId)});
      documentIds.push(documentId);
    }
    setStoppedDocumentIds(prev => new Set([...prev, ...documentIds]));
  };

  /** Stops AI generation for a single scope's current documents, leaving every other scope running. */
  const stopScope = (scopeId: string): void => {
    stopDocuments(candidate => candidate === scopeId);
    clearAiStateRef.current(scopeId);
  };

  /** Takes no argument, so a single-scope caller can pass it straight to an event handler. */
  const stopAllScopes = (): void => {
    stopDocuments(() => true);
    clearAiStateRef.current();
  };

  // ─── Auto-evaluate on save success ────────────────────────────────────────
  // Call submitEvaluations when isSaving transitions true → false without a save error.

  const submitEvaluationsRef = useRef(submitEvaluations);
  submitEvaluationsRef.current = submitEvaluations;

  const prevIsSavingRef = useRef(false);
  useEffect(() => {
    if (prevIsSavingRef.current && !isSaving && !isSaveError) {
      void submitEvaluationsRef.current();
    }
    prevIsSavingRef.current = isSaving;
  }, [isSaving, isSaveError]);

  // ─── Form state sync ──────────────────────────────────────────────────────

  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  useEffect(() => {
    onFormStateChange?.({
      isDirty: effectiveDirty,
      isLoading,
      isSaving,
      isError,
      hasErrors,
      onSave: () => onSaveRef.current(),
      onCancel: handleCancelWithAI
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveDirty, isLoading, isSaving, isError, hasErrors]);

  return {
    aiFields,
    isProcessing,
    processingScopes,
    suggestionsCount,
    suggestionsCountByScope,
    markFieldModified,
    handleCancelWithAI,
    stopScope,
    stopAllScopes,
    reindexFieldArray
  };
}
